import { selectIsAnonymousUserCriteriaFeatureFlagOn } from 'core/userContext';
import { useQueryActiveCriteriaCount } from 'modules/anonymousUserTracking/hooks/useQueryActiveCriteriaCount';
import { AUDIENCE_FIELD_NAMES, EMAIL_FIELD_NAMES } from 'modules/projectSettings/constants';
import {
  MODAL_NAMES,
  ProjectSettingsFormSchemaType,
  useProjectSettingsModalsContext,
} from 'modules/projectSettings/contexts';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { InfoBanner } from 'ui/banner';
import { FlexSpacer } from 'ui/flexSpacer';
import { ConnectedFormField, MarkdownDescription } from './ConnectedFormField';
import { DescribedCheckbox } from './DescribedCheckbox';
import { FormSection } from './FormSection';

const { ENABLE_ANON_USER_TRACKING } = AUDIENCE_FIELD_NAMES;
const { ENABLE_EMAIL_CLICK_IP_UPDATE, ENABLE_GEO_IP_LOOKUP } = EMAIL_FIELD_NAMES;

export const AUDIENCE_SECTION_TEXT = {
  sectionId: 'audience',
  sectionHeadingText: 'Audience',
  testIdPrefix: 'audience-form-section',
  /** Unknown user activation */
  enableAnonUserTrackingDescriptionText:
    'Show Unknown User Activation in the Audience tab. This will allow you to set criteria for which unknown users are added to your project.',
  enableAnonUserTrackingDescriptionTextId: 'enableAnonUserTrackingDescriptionText',
  enableAnonUserTrackingLabelText: 'Enable Unknown User Activation',
  enableAnonUserTrackingSupportUrl: 'https://support.iterable.com/hc/en-us/articles/208499956',
  enableAnonUserTrackingDisclaimer:
    'Customer is responsible for ensuring that any data collected, including data collected via SDK, and transmitted to Iterable adheres to all applicable laws, regulations, and industry standards, including, but not limited to, data privacy laws. Customer is advised to consult with their legal team to confirm that their data collection and transmission practices meet all applicable legal and regulatory obligations.',
  /** overwrite location fields */
  enableGeoIpLookupLabelText: 'Overwrite location fields',
  enableGeoIpLookupDescriptionText:
    "Every time the user's `ip` field is updated, look up and update location fields (`city`, `region`, `country`, and `timeZone`).",
  enableGeoIpLookupDescriptionTextId: 'enableGeoIpLookupDescriptionText',
  /** overwrite ip field */
  enableEmailClickIpUpdateLabelText: 'Overwrite IP field',
  enableEmailClickIpUpdateDescriptionText:
    "Every time Iterable tracks an email click event, automatically update the user's `ip` field.",
  enableEmailClickIpUpdateDescriptionTextId: 'enableEmailClickIpUpdateDescriptionText',
};

export const AudienceFormSection = () => {
  const isAnonUserTrackingEnabled = useSelector(selectIsAnonymousUserCriteriaFeatureFlagOn);

  const { showModal } = useProjectSettingsModalsContext();
  const { formState, watch } = useFormContext<ProjectSettingsFormSchemaType>();
  const { count: liveCriteriaCount } = useQueryActiveCriteriaCount();

  const isAnonUserTrackingFieldDirty = !!formState.dirtyFields[ENABLE_ANON_USER_TRACKING];
  const anonUserTrackingCheckboxValue = watch(ENABLE_ANON_USER_TRACKING);

  useEffect(() => {
    if (isAnonUserTrackingFieldDirty && !anonUserTrackingCheckboxValue && liveCriteriaCount)
      showModal({
        name: MODAL_NAMES.CONFIRM_DISABLE_ANON_USER_TRACKING,
        payload: liveCriteriaCount,
      });
  }, [anonUserTrackingCheckboxValue, isAnonUserTrackingFieldDirty, liveCriteriaCount, showModal]);

  return (
    <FormSection
      id={AUDIENCE_SECTION_TEXT.sectionId}
      data-test={`${AUDIENCE_SECTION_TEXT.testIdPrefix}`}
      headingText={AUDIENCE_SECTION_TEXT.sectionHeadingText}
    >
      {isAnonUserTrackingEnabled && (
        <ConnectedFormField name={ENABLE_ANON_USER_TRACKING}>
          <FlexSpacer alignItems="stretch" flexDirection="column" gap="base">
            <DescribedCheckbox
              data-test={`${AUDIENCE_SECTION_TEXT.testIdPrefix}-${ENABLE_ANON_USER_TRACKING}`}
              label={AUDIENCE_SECTION_TEXT.enableAnonUserTrackingLabelText}
              DescriptionText={
                <MarkdownDescription
                  id={AUDIENCE_SECTION_TEXT.enableAnonUserTrackingDescriptionTextId}
                  markdownText={AUDIENCE_SECTION_TEXT.enableAnonUserTrackingDescriptionText}
                  supportUrl={AUDIENCE_SECTION_TEXT.enableAnonUserTrackingSupportUrl}
                />
              }
              name={ENABLE_ANON_USER_TRACKING}
            />
            <InfoBanner>{AUDIENCE_SECTION_TEXT.enableAnonUserTrackingDisclaimer}</InfoBanner>
          </FlexSpacer>
        </ConnectedFormField>
      )}
      <ConnectedFormField name={ENABLE_GEO_IP_LOOKUP}>
        <DescribedCheckbox
          data-test={`${AUDIENCE_SECTION_TEXT.testIdPrefix}-${ENABLE_GEO_IP_LOOKUP}`}
          DescriptionText={
            <MarkdownDescription
              id={AUDIENCE_SECTION_TEXT.enableGeoIpLookupDescriptionTextId}
              markdownText={AUDIENCE_SECTION_TEXT.enableGeoIpLookupDescriptionText}
            />
          }
          label={AUDIENCE_SECTION_TEXT.enableGeoIpLookupLabelText}
          name={ENABLE_GEO_IP_LOOKUP}
        />
      </ConnectedFormField>
      <ConnectedFormField name={ENABLE_EMAIL_CLICK_IP_UPDATE}>
        <DescribedCheckbox
          data-test={`${AUDIENCE_SECTION_TEXT.testIdPrefix}-${ENABLE_EMAIL_CLICK_IP_UPDATE}`}
          DescriptionText={
            <MarkdownDescription
              id={AUDIENCE_SECTION_TEXT.enableEmailClickIpUpdateDescriptionTextId}
              markdownText={AUDIENCE_SECTION_TEXT.enableEmailClickIpUpdateDescriptionText}
            />
          }
          label={AUDIENCE_SECTION_TEXT.enableEmailClickIpUpdateLabelText}
          name={ENABLE_EMAIL_CLICK_IP_UPDATE}
        />
      </ConnectedFormField>
    </FormSection>
  );
};
